import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactPlayer from 'react-player';
import Dropzone from 'react-dropzone';
import cx from 'classnames';
import Icon from '@components/@core/Icon';
import ImagePicker from '@components/@core/ImagePicker';
import { convertUrl, isImageURL } from '~/services/util';

const backRatio = { 1: '100%', 2: '75%', 3: '56.2%', 4: '19.2%', 5: '79.8%' };

const Thumbnail = ({
    className = '',
    isDisablePicker = false,
    isProductConfig = false,
    size = 'small',
    type = 'default',
    source: src = '',
    overlay,
    label = '',
    title = '',
    defaultImg = '/images/no-image.jpg',
    accept = 'image/*',
    disableCrop = false,
    ratioType = '3',
    onClick = () => {},
    onDrop = () => {},
    onSubmit = () => {},
    onDelete,
}) => {
    const [source, setSource] = useState(src || defaultImg);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setSource(src || defaultImg);
    }, [src]);

    const handleDrop = (e) => {
        onDrop(e[0]);
        setOpen(true);
        if (e.length > 0) {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                onSubmit(reader.result);
            });
            reader.readAsDataURL(e[0]);
        }
    };

    const handleImageCrop = (e) => {
        setOpen(false);
        onSubmit(e);
    };

    const isImgType = (fileType) => ['image/gif', 'image/jpeg', 'image/png'].includes(fileType);

    const isVideo = source instanceof File ? !isImgType(source.type) : typeof source === 'string' && accept !== 'image/*' ? !isImageURL(source) : accept === 'video/*';
    const isIcon = /fa[a-z]\sfa-/.test(source);
    let backStyle = { backgroundColor: '$light' };
    if (source instanceof File) {
        const objURL = URL.createObjectURL(source);
        backStyle = { backgroundImage: `url('${objURL}')` };
    } else if (accept === 'image/*') {
        backStyle = { backgroundImage: `url('${source}')` };
    } else if (!isVideo) {
        backStyle = { backgroundImage: `url('${source}')` };
    }
    backStyle = { ...backStyle, paddingTop: backRatio[ratioType] };

    if (type === 'upload') {
        return (
            <Dropzone
                className="drag-drop"
                activeClassName="drag-drop active"
                acceptClassName="drag-drop active"
                rejectClassName="drag-drop deactivate"
                accept={accept}
                multiple={false}
                onDrop={handleDrop}
                noClick={source !== defaultImg}
            >
                {({ getRootProps, getInputProps }) => (
                    <section>
                        <div className={cx('ds-thumbnail', size, className)} {...getRootProps()}>
                            <input {...getInputProps()} />
                            <div className="image" style={backStyle}>
                                {source === defaultImg && isProductConfig && <div className="opaque"></div>}
                                {source ? (
                                    <>
                                        {isVideo && (
                                            <ReactPlayer
                                                id="ds-video"
                                                className="video-player"
                                                playing={false}
                                                url={source instanceof File ? URL.createObjectURL(source) : source}
                                                controls
                                                width="100%"
                                                height="100%"
                                                config={{
                                                    file: { attributes: { controlsList: 'nodownload' } },
                                                    youtube: { playerVars: { showinfo: 1 }, preload: true },
                                                }}
                                            />
                                        )}
                                        {source !== defaultImg && onDelete && (
                                            <div className="delete" onClick={onDelete}>
                                                <Icon name="fas fa-trash-can" color="#376caf" />
                                            </div>
                                        )}
                                        {source === defaultImg && isProductConfig && (
                                            <div className="upload">
                                                <Icon name="fas fa-file-arrow-up" color="#343f4b" />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className={cx('placeholder', title && 'flex-column', 'image')}>
                                        <Icon name="fas fa-file-arrow-up" color="#376caf" />
                                        {label && <span className="dsl-p16">{label}</span>}
                                        {title && <span className="dsl-m12 ms-0 mt-2">{title}</span>}
                                        <div className="delete" onClick={onDelete}>
                                            <Icon name="fas fa-pen" color="#376caf" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        {accept === 'image/*' && !isDisablePicker && open && !!source && (
                            <ImagePicker source={source} type={ratioType} disabled={disableCrop} returnBy="DataURL" onApply={(e) => setSource(e)} onComplete={handleImageCrop} />
                        )}
                    </section>
                )}
            </Dropzone>
        );
    }

    return (
        <div className={cx('ds-thumbnail', size, className)} onClick={onClick}>
            {isIcon ? (
                <div className="icon">
                    <Icon name={source} color="#969faa" />
                    {label && <p className="label">{label}</p>}
                </div>
            ) : (
                <div
                    className="image"
                    style={{
                        backgroundImage: `url('${convertUrl(source)}'), url('${defaultImg}')`,
                        paddingTop: backRatio[ratioType],
                    }}
                />
            )}
            {overlay}
        </div>
    );
};

Thumbnail.propTypes = {
    className: PropTypes.string,
    isDisablePicker: PropTypes.bool,
    type: PropTypes.oneOf(['default', 'upload']),
    size: PropTypes.oneOf(['tiny', 'small', 'regular', 'medium', 'large', 'responsive']),
    defaultImg: PropTypes.string,
    source: PropTypes.any,
    label: PropTypes.string,
    title: PropTypes.string,
    overlay: PropTypes.node,
    accept: PropTypes.string,
    disableCrop: PropTypes.bool,
    ratioType: PropTypes.oneOf(['1', '2', '3', '4', '5']),
    onDrop: PropTypes.func,
    onSubmit: PropTypes.func,
    onClick: PropTypes.func,
    onDelete: PropTypes.func,
};

export default Thumbnail;
